import { ReactNode, useState, useEffect, useRef, Children } from "react";
import { twMerge } from "tailwind-merge";
import useContainerWidthBreakpoint from "@shared/hooks/useContainerWidthBreakpoint";
import makeVideoTileClassName from "../../utils/makeVideoTileClassName";
import { PaginationDots } from "@shared/ui/PaginationDots";

export type GridProps = {
  size: number;
  children: ReactNode;
  className?: string;
  isContentShare?: boolean;
};

export function VideoUIGrid({
  size,
  children,
  className,
  isContentShare,
  ...rest
}: GridProps) {
  const ref = useRef<HTMLDivElement>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  const { containerHeight, isXs, isSm, isMd, isLg, isXl, is2xl } =
    useContainerWidthBreakpoint(ref);

  const childrenArray = Children.toArray(children);

  const gridSize = size;

  const mobileColumnSize = 2;

  const totalGroups = Math.ceil(gridSize / mobileColumnSize);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || isXs) return;

    const handleScroll = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.clientWidth;
      const currentIndex = Math.round(scrollLeft / containerWidth);
      setCurrentGroupIndex(Math.min(currentIndex, totalGroups - 1));
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [isSm, totalGroups]);

  return (
    <>
      <div
        ref={ref}
        className={twMerge(
          "flex flex-col w-full h-full max-w-screen-4xl overflow-hidden",
          !isXs && "relative",
          isContentShare && "h-auto",
          className
        )}
        {...rest}
      >
        <div
          className={twMerge(
            "flex flex-row flex-wrap justify-start items-start content-start w-full h-full",
            "sm:justify-center sm:items-center sm:content-center",
            isContentShare &&
              "flex-nowrap basis-36 sm:justify-start mb-4 h-auto overflow-x-auto"
          )}
        >
          {(() => {
            const renderTile = (child: ReactNode, index: number) => (
              <div
                key={index}
                className={makeVideoTileClassName({
                  gridSize,
                  containerHeight,
                  isXs,
                  isSm,
                  isMd,
                  isLg,
                  isXl,
                  is2xl,
                  isContentShare: isContentShare || false,
                })}
              >
                {child}
              </div>
            );

            if (!isXs && !isContentShare) {
              // Render horizontally scrollable groups of 2 when narrow viewport
              return (
                <div
                  ref={scrollContainerRef}
                  className="flex flex-row w-full h-full overflow-x-auto gap-4 pb-2 scroll-smooth snap-x snap-mandatory"
                >
                  {Array.from({ length: totalGroups }).map((_, groupIndex) => (
                    <div
                      key={`group-${groupIndex}`}
                      className="flex flex-col flex-shrink-0 h-full w-full min-w-full snap-start"
                    >
                      {childrenArray
                        .slice(
                          groupIndex * mobileColumnSize,
                          (groupIndex + 1) * mobileColumnSize
                        )
                        .map((child, itemIndex) => {
                          const index =
                            groupIndex * mobileColumnSize + itemIndex;
                          return renderTile(child, index);
                        })}
                    </div>
                  ))}
                </div>
              );
            } else {
              // Render individually when not narrow
              return childrenArray.map((child, index) =>
                renderTile(child, index)
              );
            }
          })()}
        </div>
      </div>
      {!isXs && totalGroups > 1 && !isContentShare && (
        <PaginationDots
          totalPages={totalGroups}
          currentPageIndex={currentGroupIndex}
          scrollContainerRef={scrollContainerRef}
        />
      )}
    </>
  );
}

export default VideoUIGrid;
